/**
 * Staff role helpers shared between app and server.
 * Roles live in Supabase `app_metadata.role`; the legacy `is_admin` flag
 * is kept in sync and treated as admin for backwards compatibility.
 */

export type StaffRole = 'admin' | 'manager' | 'moderator'

/** Higher rank = more privileges. Non-staff users rank 0. */
export const STAFF_ROLE_RANK: Record<StaffRole, number> = {
  admin: 3,
  manager: 2,
  moderator: 1
}

export const STAFF_ROLE_LABELS: Record<StaffRole, string> = {
  admin: 'Admin',
  manager: 'Manager',
  moderator: 'Moderator'
}

export function staffRoleRank(role: StaffRole | null | undefined): number {
  return role ? STAFF_ROLE_RANK[role] : 0
}

/** Per-role badge styling, shared by every place that renders a role pill. */
export const STAFF_ROLE_STYLES: Record<StaffRole, { icon: string, class: string }> = {
  admin: { icon: 'i-lucide-shield-alert', class: 'text-red-400 bg-red-500/10' },
  manager: { icon: 'i-lucide-shield-half', class: 'text-violet-400 bg-violet-500/10' },
  moderator: { icon: 'i-lucide-shield', class: 'text-sky-400 bg-sky-500/10' }
}

/** The lowest-ranked role in an allow-list, i.e. the minimum role that unlocks it. */
export function minStaffRole(roles: StaffRole[]): StaffRole {
  return [...roles].sort((a, b) => staffRoleRank(a) - staffRoleRank(b))[0] ?? 'admin'
}

/**
 * Short "who can see this" label for an allow-list.
 * Admin is the highest role, so an admin-only feature reads "Admin only";
 * anything else is inclusive upwards ("Moderator+" = moderators, managers and admins).
 */
export function staffAccessLabel(roles: StaffRole[]): string {
  const min = minStaffRole(roles)
  return min === 'admin' ? 'Admin only' : `${STAFF_ROLE_LABELS[min]}+`
}

/** Long form of {@link staffAccessLabel}, for tooltips. */
export function staffAccessDescription(roles: StaffRole[]): string {
  const min = minStaffRole(roles)
  if (min === 'admin') return 'Only admins can see this'
  if (min === 'manager') return 'Visible to managers and admins'
  return 'Visible to moderators, managers and admins'
}

/** Resolve the staff role from a Supabase user's app_metadata. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function resolveStaffRole(appMetadata: any): StaffRole | null {
  if (appMetadata?.role === 'admin' || appMetadata?.role === 'manager' || appMetadata?.role === 'moderator') {
    return appMetadata.role
  }
  if (appMetadata?.is_admin === true) {
    return 'admin'
  }
  return null
}
